'use client';
import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

export default function HeroSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const visualRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY;
      if (scrolled > window.innerHeight) return;
      if (textRef?.current) {
        textRef.current.style.transform = `translate3d(0, ${scrolled * 0.15}px, 0)`;
        textRef.current.style.opacity = String(Math.max(0, 1 - scrolled / (window.innerHeight * 0.8)));
      }
      if (visualRef?.current) {
        visualRef.current.style.transform = `translate3d(0, ${scrolled * -0.08}px, 0)`;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative min-h-screen flex items-end overflow-hidden"
      id="hero"
    >
      {/* Deep atmospheric background */}
      <div className="absolute inset-0 z-0">
        <div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse 80% 60% at 60% 40%, rgba(26,43,74,0.6) 0%, rgba(8,10,15,0.95) 70%)',
          }}
        />
        {/* Subtle dot grid */}
        <div
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage: 'radial-gradient(rgba(201,168,76,0.4) 1px, transparent 1px)',
            backgroundSize: '44px 44px',
            maskImage: 'radial-gradient(ellipse at 60% 40%, black 30%, transparent 75%)',
          }}
        />
        {/* Atmospheric glow orbs */}
        <div
          className="absolute top-1/4 left-1/2 w-[600px] h-[600px] rounded-full pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(26,43,74,0.5) 0%, transparent 70%)',
            transform: 'translate(-20%, -30%)',
            filter: 'blur(60px)',
          }}
        />
        <div
          className="absolute bottom-1/3 right-1/4 w-[400px] h-[400px] rounded-full pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(201,168,76,0.08) 0%, transparent 70%)',
            filter: 'blur(80px)',
          }}
        />
      </div>
      {/* Scan line on right panel */}
      <div className="absolute right-0 top-0 w-1/3 h-full overflow-hidden pointer-events-none z-0 hidden lg:block">
        <div
          className="absolute left-0 w-full h-32 animate-sys-scan"
          style={{ background: 'linear-gradient(to bottom, transparent, rgba(201,168,76,0.04), transparent)' }}
        />
      </div>
      {/* Vertical rule */}
      <div className="absolute top-0 right-[38%] h-full w-px bg-white/5 hidden lg:block overflow-hidden pointer-events-none z-0">
        <div className="absolute top-0 left-0 w-full h-96 bg-gradient-to-b from-transparent via-accent/20 to-transparent animate-sys-scan" />
      </div>
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12 pb-24 pt-36 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">

        {/* Left: Text content */}
        <div ref={textRef} className="lg:col-span-7 flex flex-col gap-6">
          {/* Label */}
          <div className="flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-accent animate-[pulse-glow_2s_infinite]" />
            <span className="font-mono text-xs text-accent uppercase tracking-widest">
              Strategic Intelligence Guide
            </span>
          </div>

          {/* Massive headline */}
          <h1
            className="font-manrope text-[11vw] lg:text-[7rem] leading-[0.88] font-semibold tracking-tighter select-none"
            style={{ willChange: 'transform' }}
          >
            <span className="block text-scan">MASTERING</span>
            <span className="block pl-8 lg:pl-20 text-gold">SWOT</span>
            <span className="block text-scan">ANALYSIS</span>
          </h1>

          {/* Subtitle */}
          <p className="mt-4 max-w-lg text-foreground/60 text-lg font-light leading-relaxed font-dm-sans">
            A step-by-step framework to turn strategic insight into confident,
            data-driven decisions for businesses, teams, and projects.
          </p>

          {/* Price + CTA */}
          <div className="mt-6 flex flex-wrap gap-4 items-center">
            <a
              href="https://strategyshelf.mysamcart.com/checkout/mastering-swot-analysis-a-practical-guide-for-success"
              className="btn-download text-background font-manrope font-bold px-8 py-4 rounded-xl text-base flex items-center gap-3 shadow-xl"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Download Now — $4.99
            </a>
            <a
              href="#preview"
              className="glass-panel px-6 py-4 rounded-xl text-foreground/70 hover:text-foreground text-sm font-medium transition-all duration-300 hover:border-accent/30 flex items-center gap-2"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
              Read Preview
            </a>
          </div>

          {/* Trust signals */}
          <div className="flex flex-wrap items-center gap-6 mt-2">
            {[
              { val: '8', label: 'Core Chapters' },
              { val: '20+', label: 'Real Frameworks' },
              { val: '$4.99', label: 'One-time Price' },
            ]?.map((item) => (
              <div key={item?.label} className="flex items-center gap-2">
                <span className="font-manrope font-semibold text-accent text-lg">{item?.val}</span>
                <span className="text-xs text-foreground/40 font-mono uppercase tracking-wider">{item?.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Floating book cover */}
        <div
          ref={visualRef}
          className="hidden lg:flex lg:col-span-5 items-end justify-center relative pointer-events-none select-none"
          style={{ perspective: '1200px' }}
        >
          {/* Glow behind book */}
          <div
            className="absolute bottom-0 left-1/2 w-64 h-64 rounded-full pointer-events-none"
            style={{
              background: 'radial-gradient(circle, rgba(201,168,76,0.2) 0%, transparent 70%)',
              filter: 'blur(40px)',
              transform: 'translateX(-50%)',
            }}
          />

          {/* Book cover */}
          <div className="animate-float-book relative" style={{ transformStyle: 'preserve-3d' }}>
            <div
              className="relative rounded-lg overflow-hidden shadow-2xl"
              style={{
                boxShadow: '0 40px 80px rgba(0,0,0,0.8), 0 0 40px rgba(201,168,76,0.15), -8px 0 20px rgba(0,0,0,0.4)',
                transform: 'rotateY(-8deg) rotateX(2deg)',
              }}
            >
              <AppImage
                src="/assets/images/Book_Cover-1772224514588.jpg"
                alt="Mastering SWOT Analysis book cover - practical guide for strategic decision making"
                width={320}
                height={420}
                className="w-64 xl:w-72 object-cover"
                priority
              />
              {/* Glossy sheen */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, transparent 50%, rgba(0,0,0,0.1) 100%)',
                }}
              />
            </div>
            {/* Book spine shadow */}
            <div
              className="absolute top-0 right-0 w-4 h-full"
              style={{
                background: 'linear-gradient(to right, rgba(0,0,0,0.4), transparent)',
                transform: 'translateX(100%) rotateY(90deg)',
                transformOrigin: 'left',
              }}
            />
          </div>

          {/* Floating stat card */}
          <div
            className="absolute top-12 -left-4 glass-gold rounded-xl px-4 py-3 animate-float-card"
            style={{ animationDelay: '1s' }}
          >
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="2.5">
                  <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/>
                  <polyline points="16 7 22 7 22 13"/>
                </svg>
              </div>
              <div>
                <div className="text-xs font-mono text-accent font-semibold">Strategic Clarity</div>
                <div className="text-[10px] text-foreground/40 font-mono">Data-driven decisions</div>
              </div>
            </div>
          </div>

          {/* Second floating card */}
          <div
            className="absolute bottom-24 -right-8 glass-gold rounded-xl px-4 py-3 animate-float-card"
            style={{ animationDelay: '2.2s' }}
          >
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="2.5">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
              </div>
              <div>
                <div className="text-xs font-mono text-accent font-semibold">Instant Access</div>
                <div className="text-[10px] text-foreground/40 font-mono">PDF + ePub formats</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none z-10"
        style={{ background: 'linear-gradient(to bottom, transparent, var(--background))' }}
      />
    </section>
  );
}